/* eslint-disable no-param-reassign */
import type { Signer } from '@ethersproject/abstract-signer'
import { getAddress } from '@ethersproject/address'
import { BigNumber, BigNumberish, formatFixed, parseFixed } from '@ethersproject/bignumber'
import { AddressZero } from '@ethersproject/constants'
import { Contract } from '@ethersproject/contracts'
import type { Provider } from '@ethersproject/providers'
import { JsonRpcSigner, Web3Provider } from '@ethersproject/providers'
import { BASE_BSC_SCAN_URLS, ChainId, Currency, ETHER, Token } from '@pancakeswap/sdk'
import memoize from 'lodash/memoize'
import { TokenAddressMap } from 'state/types'

const names = ['wei', 'kwei', 'mwei', 'gwei', 'szabo', 'finney', 'ether']

// returns the checksummed address if the address is valid, otherwise returns false
export const isAddress = memoize((value: any): string | false => {
  try {
    return getAddress(value)
  } catch {
    return false
  }
})

export function getBscScanLink(
  data: string | number,
  type: 'transaction' | 'token' | 'address' | 'block' | 'countdown',
  chainIdOverride?: number,
): string {
  const chainId = chainIdOverride || ChainId.BSC
  switch (type) {
    case 'transaction': {
      return `${BASE_BSC_SCAN_URLS[chainId]}/tx/${data}`
    }
    case 'token': {
      return `${BASE_BSC_SCAN_URLS[chainId]}/token/${data}`
    }
    case 'block': {
      return `${BASE_BSC_SCAN_URLS[chainId]}/block/${data}`
    }
    case 'countdown': {
      return `${BASE_BSC_SCAN_URLS[chainId]}/block/countdown/${data}`
    }
    default: {
      return `${BASE_BSC_SCAN_URLS[chainId]}/address/${data}`
    }
  }
}

export function getBscScanLinkForNft(collectionAddress: string, tokenId: string): string {
  return `${BASE_BSC_SCAN_URLS[ChainId.BSC]}/token/${collectionAddress}?a=${tokenId}`
}

// add 10%
export function calculateGasMargin(value: BigNumber, margin = 1000): BigNumber {
  return value.mul(BigNumber.from(10000).add(BigNumber.from(margin))).div(BigNumber.from(10000))
}

// account is not optional
export function getSigner(library: Web3Provider, account: string): JsonRpcSigner {
  return library.getSigner(account).connectUnchecked()
}

// account is optional
export function getProviderOrSigner(library: Web3Provider, account?: string): Web3Provider | JsonRpcSigner {
  return account ? getSigner(library, account) : library
}

// account is optional
export function getContract(address: string, ABI: any, signer?: Signer | Provider): Contract {
  if (!isAddress(address) || address === AddressZero) {
    throw Error(`Invalid 'address' parameter '${address}'.`)
  }

  return new Contract(address, ABI, signer)
}

export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

export function isTokenOnList(defaultTokens: TokenAddressMap, currency?: Currency): boolean {
  if (currency === ETHER) return true
  return Boolean(currency instanceof Token && defaultTokens[currency.chainId]?.[currency.address])
}

export function toLocaleString(x) {
  return x.toLocaleString('fullwide', { useGrouping: false })
}

export function formatUnits(value: BigNumberish, unitName?: string | BigNumberish): string {
  if (typeof unitName === 'string') {
    const index = names.indexOf(unitName)
    if (index !== -1) {
      unitName = 3 * index
    }
  }
  return formatFixed(value, unitName != null ? unitName : 18)
}

export function parseUnits(value: string, unitName?: BigNumberish): BigNumber {
  if (typeof value !== 'string') return null

  if (typeof unitName === 'string') {
    const index = names.indexOf(unitName)
    if (index !== -1) {
      unitName = 3 * index
    }
  }
  return parseFixed(value, unitName != null ? unitName : 18)
}

export function addCommasToNumber(number: number | string, decimalPlaces = 2): string {
  try {
    if (Number.isNaN(+number)) {
      throw new Error('Not a valid number')
    }

    // Nhân số với 10^decimalPlaces, sau đó làm tròn xuống bằng Math.floor
    const multiplier = 10 ** decimalPlaces
    const roundedNumber = Math.floor(+number * multiplier) / multiplier

    const numberWithCommas: string = roundedNumber.toFixed(decimalPlaces).replace(/\B(?=(\d{3})+(?!\d))/g, ',')

    return numberWithCommas
  } catch {
    return '0'
  }
}
